import { useEffect, useState } from "react";
import OrderChat from "../../../components/OrderChat";
import API from "../../../api/axiosConfig";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, MapPin, User, ShoppingBag, CreditCard, Camera, Printer, X, Building2, Truck, Globe, MessageCircle, Pencil, Store, Clock, CheckCircle, Navigation, XCircle } from "lucide-react";
import { getImageUrl } from "../../../utils/getImageUrl";
import OrderEditItems from "../../../components/OrderEditItems";

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ryders, setRyders] = useState([]);
  const [selectedRyder, setSelectedRyder] = useState("");
  const [sedes, setSedes] = useState([]);
  const [selectedSede, setSelectedSede] = useState("");
  const [deliveryCompanies, setDeliveryCompanies] = useState([]);
  const [deliveryMode, setDeliveryMode] = useState("free"); // "free" | "ryder" | "company"
  const [selectedDeliveryCompany, setSelectedDeliveryCompany] = useState("");
  const [comandaEnabled, setComandaEnabled] = useState(false);
  const [showComandaPreview, setShowComandaPreview] = useState(false);
  const [showChat, setShowChat] = useState(() => new URLSearchParams(window.location.search).get("chat") === "1");
  const [unreadChat, setUnreadChat] = useState(0);
  const [showEditItems, setShowEditItems] = useState(false);
  const [paperSize, setPaperSize] = useState(
    () => localStorage.getItem("comanda_paper_size") || "80mm"
  );

  const PAPER = {
    "58mm": { page: "58mm auto", margin: "3mm", bodyWidth: "52mm", fontSize: "12px", logoSize: "90px"  },
    "80mm": { page: "80mm auto", margin: "4mm", bodyWidth: "72mm", fontSize: "13px", logoSize: "130px" },
    "A4":   { page: "A4",        margin: "20mm",bodyWidth: "170mm",fontSize: "14px", logoSize: "150px" },
  };

  const handlePaperSize = (size) => {
    setPaperSize(size);
    localStorage.setItem("comanda_paper_size", size);
  };

  useEffect(() => {
    const init = async () => {
      const companyId = JSON.parse(localStorage.getItem("user"))?.company?.id;
      if (!companyId) return;
      const [sedesRes, companyRes] = await Promise.all([
        API.get(`/sedes/company/${companyId}`),
        API.get(`/companies/${companyId}`),
      ]);
      const sedesData = sedesRes.data?.data?.sedes ?? sedesRes.data?.data ?? sedesRes.data ?? [];
      setSedes(sedesData);
      if (sedesData.length === 1) setSelectedSede(sedesData[0].id);
      const c = companyRes.data?.data?.company ?? companyRes.data?.data ?? companyRes.data;
      setComandaEnabled(Boolean(c?.comandaEnabled));
    };
    init().catch(() => {});
  }, []);

  useEffect(() => {
    fetchOrder();
    fetchRyders();
    fetchDeliveryCompanies();
    // eslint-disable-next-line
  }, [id]);

  const fetchOrder = async () => {
    try {
      const res = await API.get(`/orders/${id}`);
      // ✅ Extraer del formato { success, data: { order: {...} } }
      setOrder(res.data?.data?.order ?? res.data?.data ?? res.data);
    } catch (error) {
      toast.error("Error cargando el pedido");
    } finally {
      setLoading(false);
    }
  };

  const fetchRyders = async () => {
    try {
      const companyId = JSON.parse(localStorage.getItem("user"))?.company?.id;
      const res = await API.get(
        `/ryders?companyId=${companyId}&approvalStatus=aprobado`,
      );
      setRyders(res.data?.data?.data ?? res.data?.data ?? res.data ?? []);
    } catch (error) {
      console.error("Error cargando ryders:", error);
    }
  };

  const fetchDeliveryCompanies = async () => {
    try {
      const res = await API.get("/delivery-companies/public");
      setDeliveryCompanies(res.data?.data?.companies ?? res.data?.companies ?? []);
    } catch (err) {
      console.error("fetchDeliveryCompanies:", err);
      toast.error("No se pudieron cargar las empresas de domicilio");
    }
  };

  const updateStatus = async (status) => {
    try {
      await API.patch(`/orders/${id}/status`, { status });
      toast.success("Estado actualizado");
      fetchOrder();
    } catch (error) {
      toast.error("No se pudo actualizar el estado");
    }
  };

  const markReadyForPickup = async () => {
    try {
      await API.patch(`/orders/${id}/ready-for-pickup`);
      toast.success("✅ Domiciliario notificado — el pedido está listo");
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.message || "No se pudo notificar al domiciliario");
    }
  };

  const openComandaWindow = (o) => {
    const p = PAPER[paperSize] || PAPER["80mm"];
    const itemsHtml = (o.items || []).map((item) => `
      <div class="item">
        <span class="qty">x${item.quantity}</span>
        <span class="name">${item.title}</span>
      </div>
      ${item.note ? `<div class="note">Nota: ${item.note}</div>` : ""}
    `).join("");

    const logoUrl = `${window.location.origin}/qkiere_128.png`;
    const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>Comanda #${o.orderNumber}</title>
  <style>
    @page { size: ${p.page}; margin: ${p.margin}; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Courier New', monospace; font-size: ${p.fontSize}; width: ${p.bodyWidth}; }
    .center { text-align: center; }
    .bold { font-weight: bold; }
    .xl { font-size: 20px; }
    .lg { font-size: 16px; }
    .sep { border-top: 2px dashed #000; margin: 6px 0; }
    .sep-thin { border-top: 1px dashed #555; margin: 5px 0; }
    .item { display: flex; gap: 6px; padding: 3px 0; }
    .qty { font-weight: bold; min-width: 28px; }
    .name { flex: 1; }
    .note { font-size: 11px; padding: 1px 0 3px 34px; font-style: italic; }
    .row { display: flex; justify-content: space-between; padding: 2px 0; }
    .logo { display: block; width: ${p.logoSize}; height: ${p.logoSize}; object-fit: contain; margin: 0 auto; filter: brightness(0); }
  </style>
</head>
<body>
  <div class="center">
    <img class="logo" src="${logoUrl}" alt="Qkiere" />
  </div>
  <div class="center bold xl">COMANDA</div>
  <div class="center bold lg">Pedido #${o.orderNumber}</div>
  <div class="center">${new Date(o.createdAt).toLocaleString("es-CO")}</div>
  <div class="sep"></div>
  ${itemsHtml}
  <div class="sep"></div>
  <div class="row"><span>Cliente:</span><span class="bold">${o.user?.name || "—"}</span></div>
  ${o.deliveryAddress ? `<div class="row"><span>Dirección:</span><span>${o.deliveryAddress}</span></div>` : ""}
  ${o.deliveryPhone ? `<div class="row"><span>Tel:</span><span>${o.deliveryPhone}</span></div>` : ""}
  ${o.note ? `<div class="sep-thin"></div><div>Nota del pedido: <strong>${o.note}</strong></div>` : ""}
  <div class="sep"></div>
  <div class="center">— Qkiere.com —</div>
</body>
</html>`;

    // Blob con charset UTF-8 explícito → corrige ñ, á, é, etc. que document.write() pierde
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const blobUrl = URL.createObjectURL(blob);

    // Reutiliza o crea el iframe oculto (evita ventana emergente)
    const existing = document.getElementById("__comanda_print_frame__");
    if (existing) existing.remove();

    const iframe = document.createElement("iframe");
    iframe.id = "__comanda_print_frame__";
    iframe.style.cssText =
      "position:absolute;top:0;left:0;width:1px;height:1px;border:0;opacity:0;pointer-events:none;";
    iframe.src = blobUrl;
    document.body.appendChild(iframe);

    const cleanup = () => {
      if (document.body.contains(iframe)) iframe.remove();
      URL.revokeObjectURL(blobUrl);
    };

    iframe.onload = () => {
      // Pequeño delay para que cargue el logo antes de imprimir
      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          iframe.contentWindow.addEventListener("afterprint", cleanup, { once: true });
        } catch (_) {
          cleanup();
          toast.warning("No se pudo imprimir. Revisa que el navegador tenga permisos.");
        }
        // Limpieza de seguridad si afterprint no dispara (algunos navegadores)
        setTimeout(cleanup, 120000);
      }, 500);
    };
  };

  if (loading) return <p className="text-center mt-20">Cargando pedido...</p>;
  if (!order) return <p className="text-center mt-20">Pedido no encontrado</p>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">

      {/* ── Modal vista previa comanda ── */}
      {showComandaPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            {/* Header del modal */}
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <span className="font-bold text-gray-800 flex items-center gap-2">
                <Printer size={18} className="text-gray-500" />
                Vista previa — Comanda
              </span>
              <button onClick={() => setShowComandaPreview(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            {/* Selector de tamaño de papel */}
            <div className="px-5 pt-4 pb-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tamaño de papel</span>
                <span className="text-xs text-green-600 font-semibold flex items-center gap-1">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><path d="M1 5l3 3 5-5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Predeterminado guardado
                </span>
              </div>
              <div className="flex gap-2">
                {Object.keys(PAPER).map((size) => (
                  <button
                    key={size}
                    onClick={() => handlePaperSize(size)}
                    className={`flex-1 py-1.5 rounded-xl text-xs font-bold border transition ${
                      paperSize === size
                        ? "bg-gray-800 text-white border-gray-800 shadow-sm"
                        : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                    }`}
                  >
                    {size}
                    {paperSize === size && <span className="ml-1 opacity-70">✓</span>}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1.5">Se recuerda para todas las comandas</p>
            </div>

            {/* Ticket simulado */}
            <div className="px-5 py-3 overflow-y-auto max-h-[65vh]">
              <div
                className="mx-auto border border-dashed border-gray-300 rounded-lg p-4 bg-gray-50"
                style={{
                  fontFamily: "'Courier New', monospace",
                  fontSize: paperSize === "58mm" ? 11 : paperSize === "A4" ? 14 : 13,
                  maxWidth: paperSize === "58mm" ? 220 : paperSize === "A4" ? 420 : 280,
                }}
              >
                {/* Encabezado */}
                <div className="text-center">
                  <img src="/qkiere_128.png" alt="Qkiere" className="w-24 h-24 object-contain mx-auto" style={{ filter: 'brightness(0)' }} />
                </div>
                <p className="text-center font-black text-lg tracking-wide">COMANDA</p>
                <p className="text-center font-bold text-base">Pedido #{order.orderNumber}</p>
                <p className="text-center text-xs text-gray-500">
                  {new Date(order.createdAt).toLocaleString("es-CO")}
                </p>

                {/* Separador */}
                <div className="border-t-2 border-dashed border-gray-400 my-2" />

                {/* Ítems */}
                {(order.items || []).map((item, i) => (
                  <div key={i} className="mb-1">
                    <div className="flex gap-2">
                      <span className="font-bold min-w-[28px]">x{item.quantity}</span>
                      <span className="flex-1">{item.title}</span>
                    </div>
                    {item.note && (
                      <p className="text-xs italic text-gray-500 pl-8">
                        Nota: {item.note}
                      </p>
                    )}
                  </div>
                ))}

                {/* Separador */}
                <div className="border-t-2 border-dashed border-gray-400 my-2" />

                {/* Datos cliente */}
                <div className="flex justify-between text-xs">
                  <span>Cliente:</span>
                  <span className="font-bold">{order.user?.name || "—"}</span>
                </div>
                {order.deliveryAddress && (
                  <div className="flex justify-between text-xs">
                    <span>Dirección:</span>
                    <span className="text-right ml-2 max-w-[160px] break-words">{order.deliveryAddress}</span>
                  </div>
                )}
                {order.deliveryPhone && (
                  <div className="flex justify-between text-xs">
                    <span>Tel:</span>
                    <span>{order.deliveryPhone}</span>
                  </div>
                )}
                {order.note && (
                  <>
                    <div className="border-t border-dashed border-gray-300 my-2" />
                    <p className="text-xs">
                      <strong>Nota:</strong> {order.note}
                    </p>
                  </>
                )}

                {/* Pie */}
                <div className="border-t-2 border-dashed border-gray-400 my-2" />
                <p className="text-center text-xs text-gray-400">— Qkiere.com —</p>
              </div>
            </div>

            {/* Acciones */}
            <div className="flex gap-3 px-5 py-4 border-t bg-gray-50">
              <button
                onClick={() => setShowComandaPreview(false)}
                className="flex-1 py-2 rounded-xl border border-gray-300 text-gray-600 text-sm font-semibold hover:bg-gray-100 transition"
              >
                Cerrar
              </button>
              <button
                onClick={() => { openComandaWindow(order); setShowComandaPreview(false); }}
                className="flex-1 py-2 rounded-xl bg-gray-800 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-gray-900 transition"
              >
                <Printer size={15} /> Imprimir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Barra superior: Volver + Acciones ── */}
      <div className="sticky top-14 z-30 bg-white/95 backdrop-blur-sm pb-4 mb-4 -mx-6 px-6 pt-3 border-b border-gray-200 shadow-sm">
        <button
          className="flex items-center gap-1.5 text-supernova-600 hover:text-supernova-700 font-semibold mb-3 text-sm"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={17} /> Volver
        </button>

        {!["entregado", "cancelado"].includes(order?.status) && (
          <>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Acciones</p>
          <div className="flex gap-2 flex-wrap">

            {/* ── PICKUP ── */}
            {order?.deliveryType === "recoger_en_tienda" && order?.status === "confirmado" && (
              <button
                onClick={() => updateStatus("en_camino")}
                className="flex-1 min-w-[160px] flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm shadow-lg shadow-amber-400/50 ring-2 ring-amber-400 ring-offset-1 animate-pulse transition"
              >
                <CheckCircle size={17} /> Listo para retirar
              </button>
            )}

            {order?.deliveryType === "recoger_en_tienda" && order?.status === "en_camino" && (
              <button
                onClick={() => updateStatus("entregado")}
                className="flex-1 min-w-[160px] flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold text-sm shadow-lg shadow-green-400/40 ring-2 ring-green-500 ring-offset-1 animate-pulse transition"
              >
                <CheckCircle size={17} /> Retirado por el cliente
              </button>
            )}

            {/* ── DOMICILIO ── */}
            {order?.deliveryType !== "recoger_en_tienda" && order?.status === "asignado" && (
              <button
                onClick={markReadyForPickup}
                className="flex-1 min-w-[160px] flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-green-500 hover:bg-green-600 text-white font-bold text-sm shadow-lg shadow-green-400/50 ring-2 ring-green-400 ring-offset-1 animate-pulse transition"
              >
                <CheckCircle size={17} /> Listo para retirar
              </button>
            )}

            {order?.deliveryType !== "recoger_en_tienda" && ["confirmado", "listo", "en_camino"].includes(order?.status) && (
              <button
                onClick={() => updateStatus("en_camino")}
                className="flex-1 min-w-[140px] flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm shadow-md transition"
              >
                <Navigation size={16} /> Entregado a domiciliario
              </button>
            )}

            {/* Cancelar — siempre presente */}
            <button
              onClick={async () => {
                if (!window.confirm("¿Cancelar este pedido?")) return;
                await updateStatus("cancelado");
              }}
              className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold text-sm shadow-md transition min-w-[130px]"
            >
              <XCircle size={16} /> Cancelar
            </button>
          </div>
          </>
        )}
      </div>

      <div className="bg-white shadow-lg rounded-2xl p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Pedido #{order.orderNumber}
            </h1>
            <p className="text-gray-500 mt-1">
              Creado el: {new Date(order.createdAt).toLocaleString("es-CO")}
            </p>
          </div>
          {comandaEnabled && (
            <button
              onClick={() => setShowComandaPreview(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-gray-300 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition flex-shrink-0"
            >
              <Printer size={15} />
              Comanda
            </button>
          )}
        </div>

        <span
          className={`inline-block mt-3 px-4 py-1 rounded-full text-white text-sm font-bold
            ${
              order.status === "pendiente"
                ? "bg-yellow-500"
                : order.status === "confirmado"
                  ? "bg-blue-500"
                  : order.status === "asignado"
                    ? "bg-purple-600"
                    : order.status === "en_camino"
                      ? "bg-orange-500"
                      : order.status === "entregado"
                        ? "bg-green-600"
                        : "bg-red-600"
            }`}
        >
          {order.status.toUpperCase()}
        </span>
      </div>

      <div className="bg-white shadow rounded-2xl p-6 mb-6">
        <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
          <User size={20} className="text-supernova-600" /> Cliente
        </h3>
        <p>
          <strong>Nombre:</strong> {order.user?.name}
        </p>
      </div>

      {order.deliveryType === "recoger_en_tienda" ? (
        <div className="bg-blue-50 border border-blue-200 shadow rounded-2xl p-6 mb-6">
          <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Store size={20} className="text-blue-600" />
            Retiro en tienda
            <span className="text-[11px] font-bold bg-blue-600 text-white px-2 py-0.5 rounded-full tracking-wide">
              PICKUP
            </span>
          </h3>
          {(order.pickupSede ?? order.sede) ? (
            <>
              <p className="font-semibold text-gray-800">
                {(order.pickupSede ?? order.sede).name}
              </p>
              {(order.pickupSede ?? order.sede).address && (
                <p className="text-sm text-gray-500 mt-0.5">
                  {(order.pickupSede ?? order.sede).address}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-500">El cliente retira en tienda</p>
          )}
          {order.deliveryPhone && (
            <p className="text-sm text-gray-500 mt-1">📞 {order.deliveryPhone}</p>
          )}
        </div>
      ) : (
        <div className="bg-white shadow rounded-2xl p-6 mb-6">
          <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
            <MapPin size={20} className="text-supernova-600" /> Dirección de entrega
          </h3>
          {order.deliveryAddress && <p>{order.deliveryAddress}</p>}
          {order.deliveryCity && <p className="text-sm text-gray-500">{order.deliveryCity}</p>}
          {order.deliveryPhone && <p className="text-sm text-gray-500">📞 {order.deliveryPhone}</p>}
        </div>
      )}

      <div className="bg-white shadow rounded-2xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-800 flex items-center gap-2">
            <ShoppingBag size={20} className="text-supernova-600" /> Productos
          </h3>
          {["pendiente", "confirmado"].includes(order.status) && (
            <button
              onClick={() => setShowEditItems(true)}
              className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 border border-blue-200 rounded-lg px-3 py-1.5 hover:bg-blue-50 transition-colors"
            >
              <Pencil size={12} /> Editar items
            </button>
          )}
        </div>

        {order.items.map((item) => (
          <div
            key={item.id}  // ✅ usar item.id (UUID), no item.product (MongoDB ref)
            className="flex items-center gap-3 mb-4 border-b pb-3"
          >
            <img
              src={item.image ? getImageUrl(item.image) : "/placeholder.png"}
              alt={item.title}
              className="w-16 h-16 rounded-lg object-cover"
            />
            <div className="flex-1">
              <p className="font-semibold">{item.title}</p>
              <p className="text-gray-500 text-sm">x{item.quantity}</p>
              {item.note && <p className="text-xs text-amber-600 mt-0.5 italic">📝 {item.note}</p>}
            </div>
            <p className="font-bold text-supernova-600">
              ${(item.price * item.quantity).toLocaleString("es-CO")}
            </p>
          </div>
        ))}
      </div>

      <div className="bg-white shadow rounded-2xl p-6 mb-6">
        <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
          <CreditCard size={20} className="text-supernova-600" /> Pago
        </h3>

        <p>
          <strong>Método:</strong> {order.paymentMethod}
        </p>

        <p>
          <strong>Subtotal:</strong> ${Number(order.subtotal).toLocaleString("es-CO")}
        </p>

        {/* ✅ couponCode y couponDiscount son campos directos en la orden */}
        {order.couponCode && Number(order.couponDiscount) > 0 && (
          <p className="text-green-600 font-semibold">
            Cupón aplicado ({order.couponCode}): − $
            {Number(order.couponDiscount).toLocaleString("es-CO")}
          </p>
        )}

        {order.qpointsUsed > 0 && (
          <p className="text-green-700 font-semibold flex items-center gap-2">
            <span>QPUNTOS usados:</span>
            <span>− ${order.qpointsUsed.toLocaleString("es-CO")}</span>
          </p>
        )}

        <p>
          <strong>Domicilio:</strong> $
          {Number(order.deliveryFee).toLocaleString("es-CO")}
        </p>

        <hr className="my-2" />

        <p className="font-bold text-lg">
          Total: ${Number(order.total).toLocaleString("es-CO")}
        </p>
      </div>


      {/* Foto de entrega */}
      {order.status === "entregado" && order.deliveryPhoto && (
        <div className="bg-white shadow rounded-2xl p-6 mb-6">
          <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Camera size={20} className="text-green-600" /> Foto de entrega
          </h3>
          <p className="text-xs text-gray-400 mb-3">
            Tomada por el domiciliario al momento de entregar el pedido
            {order.deliveredAt && (
              <> &middot; {new Date(order.deliveredAt).toLocaleString("es-CO")}</>
            )}
          </p>
          <img
            src={getImageUrl(order.deliveryPhoto)}
            alt="Foto de entrega"
            className="w-full max-w-sm rounded-xl border border-gray-200 object-cover"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        </div>
      )}

      {/* ── Sección despacho / confirmación ── */}
      {["pendiente", "confirmado", "asignado", "en_camino", "entregado"].includes(order.status) && (
        <div className="airmail-border mb-8">
          <div className="p-6 shadow-md rounded-xl">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <MapPin size={22} className="text-supernova-600" />
              {order.deliveryType === "recoger_en_tienda" ? "Retiro en tienda" : "Despacho del pedido"}
            </h3>

            {/* ──────────── PENDIENTE ──────────── */}
            {order.status === "pendiente" && (
              <>
                {order.deliveryType === "recoger_en_tienda" ? (
                  /* Pickup: el cliente ya eligió la sede — aceptar pedido */
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                      <p className="text-xs text-blue-500 font-semibold uppercase tracking-wide mb-1">Sede elegida por el cliente</p>
                      <p className="font-bold text-gray-800">
                        {order.sede?.name ?? order.pickupSede?.name ?? "—"}
                      </p>
                      {(order.sede?.address ?? order.pickupSede?.address) && (
                        <p className="text-sm text-gray-500">
                          {order.sede?.address ?? order.pickupSede?.address}
                        </p>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      El cliente retirará el pedido en tu tienda. Acepta el pedido para comenzar a prepararlo.
                    </p>
                    <button
                      onClick={async () => {
                        try {
                          // Al crear el pedido la sede queda en sedeId; pickupSedeId se llena tras el dispatch
                          const sedeId = order.sede?.id ?? order.pickupSede?.id;
                          if (!sedeId) return toast.error("No se encontró la sede del cliente");
                          await API.patch(`/orders/${order.id}/dispatch`, { sedeId });
                          toast.success("Pedido aceptado — comenzando preparación");
                          if (comandaEnabled) openComandaWindow(order);
                          fetchOrder();
                        } catch (err) {
                          toast.error(err.response?.data?.message || "Error al aceptar");
                        }
                      }}
                      className="w-full py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
                    >
                      Aceptar pedido
                    </button>
                  </div>
                ) : (
                  /* Domicilio: seleccionar sede + modo de asignación */
                  <>
                    {/* Sede de despacho */}
                    <div className="mb-5">
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Sede de despacho <span className="text-red-500">*</span>
                      </label>
                      {sedes.length === 1 ? (
                        <div className="w-full border border-indigo-200 bg-indigo-50 rounded-lg p-3 text-sm text-indigo-800 font-medium">
                          {sedes[0].name} — {sedes[0].address}
                        </div>
                      ) : (
                        <select
                          value={selectedSede}
                          onChange={(e) => setSelectedSede(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        >
                          <option value="">Seleccionar sede</option>
                          {sedes.map((sede) => (
                            <option key={sede.id} value={sede.id}>
                              {sede.name} — {sede.address}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    {/* Modo de asignación del domicilio */}
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        ¿Cómo se asigna el domicilio?
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {[
                          { key: "free",    icon: Globe,      label: "Libre",             desc: "Cualquier domiciliario puede tomarlo" },
                          { key: "ryder",   icon: Truck,      label: "Domiciliario",       desc: "Asignar a uno de tus ryders disponibles" },
                          { key: "company", icon: Building2,  label: "Empresa de domicilio", desc: "Enviar a una empresa de la plataforma" },
                        ].map(({ key, icon: Icon, label, desc }) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => {
                              setDeliveryMode(key);
                              setSelectedRyder("");
                              setSelectedDeliveryCompany("");
                            }}
                            className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 text-center transition
                              ${deliveryMode === key
                                ? "border-indigo-500 bg-indigo-50"
                                : "border-gray-200 hover:border-indigo-300 hover:bg-gray-50"}`}
                          >
                            <Icon size={20} className={deliveryMode === key ? "text-indigo-600" : "text-gray-400"} />
                            <span className={`text-xs font-bold ${deliveryMode === key ? "text-indigo-700" : "text-gray-700"}`}>{label}</span>
                            <span className="text-[10px] text-gray-400 leading-tight">{desc}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Selector condicional */}
                    {deliveryMode === "ryder" && (
                      <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Seleccionar domiciliario</label>
                        <select
                          value={selectedRyder}
                          onChange={(e) => setSelectedRyder(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        >
                          <option value="">Dejar libre si ninguno disponible</option>
                          {ryders.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name} — {r.vehicleType} {r.vehiclePlate ? `· ${r.vehiclePlate}` : ""}
                            </option>
                          ))}
                        </select>
                        {ryders.length === 0 && (
                          <p className="text-xs text-amber-600 mt-1">Sin domiciliarios disponibles ahora. El pedido quedará libre.</p>
                        )}
                      </div>
                    )}

                    {deliveryMode === "company" && (
                      <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Seleccionar empresa de domicilio</label>
                        {deliveryCompanies.length === 0 ? (
                          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-500 text-center">
                            No hay empresas de domicilio disponibles en la plataforma.
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 gap-2 max-h-44 overflow-y-auto">
                            {deliveryCompanies.map((dc) => (
                              <button
                                key={dc.id}
                                type="button"
                                onClick={() => setSelectedDeliveryCompany(dc.id)}
                                className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition
                                  ${selectedDeliveryCompany === dc.id
                                    ? "border-indigo-500 bg-indigo-50"
                                    : "border-gray-200 hover:border-indigo-300"}`}
                              >
                                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                                  {dc.name.slice(0, 2).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <p className="font-semibold text-gray-900 text-sm truncate">{dc.name}</p>
                                    {dc.verified && <span className="text-[10px] text-blue-600 bg-blue-50 font-bold px-1.5 py-0.5 rounded-full shrink-0">✓</span>}
                                  </div>
                                  <p className="text-xs text-gray-500">{dc.city ?? ""} · {dc._count?.ryders ?? 0} domiciliarios</p>
                                </div>
                                {selectedDeliveryCompany === dc.id && (
                                  <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center shrink-0">
                                    <span className="text-white text-[10px] font-bold">✓</span>
                                  </div>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                        {selectedDeliveryCompany && (
                          <p className="text-xs text-indigo-600 mt-1.5 flex items-center gap-1">
                            <Building2 size={11} />
                            Solo los domiciliarios de esta empresa verán el pedido.
                          </p>
                        )}
                      </div>
                    )}

                    <button
                      onClick={async () => {
                        if (!selectedSede) return toast.error("La sede de despacho es obligatoria");
                        if (deliveryMode === "company" && !selectedDeliveryCompany)
                          return toast.error("Selecciona una empresa de domicilio");
                        try {
                          const payload = { sedeId: selectedSede };
                          if (deliveryMode === "ryder" && selectedRyder)
                            payload.ryderId = selectedRyder;
                          if (deliveryMode === "company" && selectedDeliveryCompany)
                            payload.deliveryCompanyId = selectedDeliveryCompany;

                          await API.patch(`/orders/${order.id}/dispatch`, payload);

                          const msg = deliveryMode === "ryder" && selectedRyder
                            ? "Pedido confirmado y domiciliario asignado"
                            : deliveryMode === "company"
                              ? "Pedido enviado a la empresa de domicilio"
                              : "Pedido confirmado — libre para domiciliarios";

                          toast.success(msg);
                          if (comandaEnabled) openComandaWindow(order);
                          fetchOrder();
                        } catch (err) {
                          toast.error(err.response?.data?.message || "Error confirmando el despacho");
                        }
                      }}
                      className="w-full py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
                    >
                      Confirmar despacho
                    </button>
                  </>
                )}
              </>
            )}

            {/* ──────────── CONFIRMADO / ASIGNADO / EN CAMINO / ENTREGADO ──────────── */}
            {order.status !== "pendiente" && (
              <div className="space-y-5">
                {order.pickupCode && (
                  <div className={`rounded-xl p-4 border-2 text-center ${
                    order.pickupCodeVerifiedAt ? "bg-green-50 border-green-300" : "bg-amber-50 border-amber-400"
                  }`}>
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Código de recogida</p>
                    <p className="text-5xl font-black tracking-widest text-gray-800 my-2">{order.pickupCode}</p>
                    {order.pickupCodeVerifiedAt ? (
                      <p className="text-xs text-green-600 font-semibold">✓ Verificado por el domiciliario</p>
                    ) : (
                      <p className="text-xs text-amber-600">
                        {order.deliveryType === "recoger_en_tienda"
                          ? "Pídele este código al cliente para confirmar el retiro"
                          : "Dáselo al domiciliario cuando llegue a recoger"}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <p className="text-sm text-gray-500">
                    {order.deliveryType === "recoger_en_tienda" ? "Sede de retiro" : "Sede de despacho"}
                  </p>
                  <p className="font-semibold text-gray-800">
                    {order.pickupSede?.name ?? order.sede?.name ?? "—"}
                  </p>
                  <p className="text-sm text-gray-600">
                    {order.pickupSede?.address ?? order.sede?.address}
                  </p>
                </div>

                {order.deliveryType !== "recoger_en_tienda" && (
                  <div>
                    <p className="text-sm text-gray-500">Domiciliario</p>
                    {order.ryder ? (
                      <>
                        <p className="font-semibold text-gray-800">{order.ryder.user?.name}</p>
                        <p className="text-sm text-gray-600">Tel: {order.ryder.user?.phone}</p>
                        <p className="text-sm text-gray-600">
                          {order.ryder.vehicleType} · {order.ryder.vehicleBrand} {order.ryder.vehicleModel} · Placa {order.ryder.vehiclePlate}
                        </p>
                      </>
                    ) : order.deliveryCompany ? (
                      <div className="flex items-start gap-2 mt-1">
                        <Building2 size={16} className="text-indigo-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-gray-800">{order.deliveryCompany.name}</p>
                          {order.deliveryCompany.phone && (
                            <p className="text-sm text-gray-600">Tel: {order.deliveryCompany.phone}</p>
                          )}
                          <p className="text-xs text-indigo-600 flex items-center gap-1 mt-0.5">
                            <Clock size={11} />
                            Empresa de delivery asignada — esperando ryder
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="italic text-gray-500">Pedido libre (sin domiciliario asignado)</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}


      {/* ── Chat con el cliente ── */}
      {!["cancelado"].includes(order.status) && (
        <div className="shadow rounded-2xl p-6 mb-6 bg-white">
          <button
            onClick={() => { setShowChat((v) => !v); setUnreadChat(0); }}
            className="flex items-center justify-between w-full"
          >
            <h3 className="font-bold text-gray-800 flex items-center gap-2">
              <MessageCircle size={20} className="text-blue-500" />
              Chat con el cliente
              {unreadChat > 0 && (
                <span className="ml-1 bg-blue-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                  {unreadChat} nuevo{unreadChat > 1 ? "s" : ""}
                </span>
              )}
            </h3>
            <span className="text-xs text-gray-400">{showChat ? "▲ Ocultar" : "▼ Ver chat"}</span>
          </button>
          {showChat && (
            <div className="mt-4">
              <OrderChat
                orderId={order.id}
                kind="store"
                user={{ role: "company" }}
                isActive={!["entregado", "cancelado"].includes(order.status)}
                onNewMessage={() => setUnreadChat((c) => c + 1)}
              />
            </div>
          )}
        </div>
      )}

      {/* ── Modal editar items ── */}
      {showEditItems && (
        <OrderEditItems
          order={order}
          onClose={() => setShowEditItems(false)}
          onSaved={(updated) => {
            if (updated) setOrder(updated);
            else fetchOrder();
          }}
        />
      )}

    </div>
  );
}
